/**
 * What the hero can honestly say, from what the viewer can see (B1).
 *
 * The board snapshot is an approver artefact, so for every other
 * position it 403s -- and the hero used to fall back to `?? "green"`,
 * printing "NORMAL · All services steady · 0/0 IBSes impacted · RATED
 * GREEN" above a card saying eight IBSes were impacted. It told a
 * Resilience Lead her bank was fine because she was not allowed to be
 * told otherwise.
 *
 * The cascade IS readable by anyone with the Twin module and already
 * carries the resilience RAG counts (P258), the impacted IBS count and
 * the revenue at risk. So the fallback is not a shrug: it is the same
 * picture, derived from the source the cascade card on the same screen
 * is reading -- which is what makes the two agree.
 */
import type { BoardSnapshot, CascadeSummary, Posture } from "shared/api/types";

/**
 * RAG, plus the two honest states that are not colours.
 *
 * `unknown` -- nothing could be READ (the board 403s for this seat and
 * the cascade did not answer).
 *
 * `not_assessed` -- round 12 B1. Both sources answered, and what they
 * said is that there is nothing to judge: no service admitted, no
 * tolerance declared, no telemetry sampled. Different from `unknown`,
 * and very different from green, which is what this returned.
 */
export type PostureTone = "red" | "amber" | "green" | "unknown" | "not_assessed";

export interface HeroPosture {
  rag: PostureTone;
  impacted: number;
  total: number;
  /** Raw decimal string, or null when the figure is not measured. */
  revenueAtRisk: string | null;
  mvbImpacted: number;
  mvbTotal: number;
  /** True when the board was unavailable and this came from the cascade. */
  fromCascade: boolean;
}

/**
 * Prefer the board; fall back to the cascade; say nothing rather than
 * guess when neither answered.
 *
 * Stitching review 5.4 -- the COUNTS come from the posture whenever it
 * answered, whoever is reading: the register, the impacted services
 * and the MVB pair, the same figures the board prints. Only the colour
 * and the revenue keep the board-then-cascade order, because the posture
 * carries neither. A seat that cannot open the board now reads "5 of 9"
 * where it read 5 with no denominator.
 */
export function derivePosture(
  board: BoardSnapshot | undefined,
  cascade: CascadeSummary | undefined,
  posture?: Posture | undefined,
): HeroPosture {
  const base = deriveFromBoardOrCascade(board, cascade);
  // A partial answer (a stub, an older backend) is not a posture: read
  // it only when the three figures this needs are all there.
  if (
    !posture?.impacted_services ||
    !posture.register_services ||
    !posture.mvb_impacted ||
    base.rag === "unknown"
  ) {
    return base;
  }
  return {
    ...base,
    impacted: posture.impacted_services.count,
    total: posture.register_services.count,
    mvbImpacted: posture.mvb_impacted.count,
    mvbTotal: posture.mvb_impacted.of ?? 0,
  };
}

function deriveFromBoardOrCascade(
  board: BoardSnapshot | undefined,
  cascade: CascadeSummary | undefined,
): HeroPosture {
  const h = board?.headline;
  if (h) {
    return {
      rag: h.overall_rag,
      impacted: h.ibs_impacted,
      total: h.ibs_count,
      revenueAtRisk: h.revenue_at_risk_annual ?? null,
      mvbImpacted: h.mvb_impacted_count ?? 0,
      mvbTotal: h.mvb_total_count ?? 0,
      fromCascade: false,
    };
  }
  if (cascade) {
    // The same resilience counts the cascade card renders beside this.
    const red = cascade.ibs_resilience_red ?? 0;
    const amber = cascade.ibs_resilience_amber ?? 0;
    const impacted = cascade.ibs_impacted_count ?? 0;
    return {
      /* B1 -- "0 red, 0 amber" over an empty estate is the same zero as
         "0 red, 0 amber" over a healthy one, and this returned green
         for both. The cascade says which it is now. */
      rag:
        red > 0
          ? "red"
          : amber > 0
            ? "amber"
            : cascade.estate_assessed === false
              ? "not_assessed"
              : "green",
      impacted,
      // The cascade counts what is IMPACTED, not the estate; red+amber+
      // impacted is not a denominator, so there is none to show.
      total: 0,
      revenueAtRisk: cascade.total_annual_revenue_at_risk ?? null,
      // The cascade carries the same MVB intersection the board does
      // (P20), so this half of the strip is not lost either.
      mvbImpacted: cascade.mvb_impacted_count ?? 0,
      mvbTotal: cascade.mvb_total_count ?? 0,
      fromCascade: true,
    };
  }
  return {
    rag: "unknown",
    impacted: 0,
    total: 0,
    revenueAtRisk: null,
    mvbImpacted: 0,
    mvbTotal: 0,
    fromCascade: false,
  };
}
